/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useSettings } from '@/data/Settings';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsLoginIdentity } from 'integration/generated/transactions';
import { ComIbmCommerceRestMemberHandlerLoginIdentityHandlerLoginForm } from 'integration/generated/transactions/data-contracts';
import { isErrorType, processError } from '@/data/utils/processError';
import { useNotifications } from '@/data/Content/Notifications';
import { ErrorType } from '@/data/types/Error';
import { PASSWORD_EXPIRED } from '@/data/constants/errors';
import { useState } from 'react';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { getLocalization } from '@/data/Localization';
import { ContentProps } from '@/data/types/ContentProps';
import { GetServerSidePropsContext } from 'next';
import { error as logError } from '@/data/utils/loggerUtil';

export { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';

export const loginFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComIbmCommerceRestMemberHandlerLoginIdentityHandlerLoginForm,
		params: RequestParams
	) => {
		try {
			const resp = await transactionsLoginIdentity(pub).loginIdentityLogin(
				storeId,
				query,
				data,
				params
			);
			return resp;
		} catch (error: any) {
			logError(context?.req, 'Login: loginFetcher: error: %o', error);
			throw error;
		}
	};

export type UserLogon = {
	logonId?: string;

	email?: string;
	logonPassword: string;
	rememberMe: boolean;
	logonPasswordNew?: string;
	logonPasswordVerify?: string;
	changePassword?: string;
	/**
	 * @deprecated do not use.
	 */
};

export const getLogin = async ({ cache, context }: ContentProps) => {
	await Promise.all([
		getLocalization(cache, context.locale || 'en-US', 'SignIn'),
		getLocalization(cache, context.locale || 'en-US', 'RegistrationB2BLayout'),
	]);
};

export const useLogin = () => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const [passwordExpired, setPasswordExpired] = useState<(ErrorType & { user: UserLogon }) | null>(
		null
	);
	const [logonIdTemp, setLogonIdTemp] = useState<string>('');
	const [countError, setCountError] = useState<number>(0);

	// eslint-disable-next-line complexity
	const loginSubmit = async (props: UserLogon) => {
		const { email = '', logonId: _logonId, rememberMe, ...rest } = props;
		const logonId: string = _logonId || email;
		setLogonIdTemp(_logonId || email);
		try {
			const resp = await loginFetcher(true)(
				settings?.storeId ?? '',
				{ rememberMe: props.rememberMe, updateCookies: 'true' },
				{ logonId, ...rest },
				params
			);
			return resp;
		} catch (e) {
			const error: TransactionErrorResponse | ErrorType = processError(
				e as TransactionErrorResponse
			);
			setCountError(countError + 1);

			if (logonIdTemp === logonId && countError > 3) {
				notifyError('Espere unos segundos antes de intentar iniciar la sesión otra vez');
			} else {
				if (logonIdTemp !== logonId) {
					setCountError(0);
				}
				if (isErrorType(error) && error.error.errorKey === PASSWORD_EXPIRED) {
					setPasswordExpired({ ...error, user: { ...props } });
				} else {
					if (props.changePassword) {
						const changePasswordx = true;
						const title = '';
						notifyError(error, title, changePasswordx);
					} else {
						notifyError(error);
					}
				}
			}
			return error;
		}
	};

	return {
		loginSubmit,
		passwordExpired,
		setPasswordExpired,
	};
};
